/**
 * AtlasVoiceRoomsModule — VOICE ROOMS (2026-09-24): "implement voice rooms with an SFU". The seats are SpacetimeDB rows
 * (server/src/reducers/voice_rooms.ts), the audio rides the SFU (server/workers/voice-sfu, mediasoup) through
 * `lib/voice/sfu_client.ts`, the rules are `lib/voice_room_rules.ts`. Proximity voice (AtlasVoiceModule, the P2P
 * mesh) is untouched: a room is a second, non-spatial layer — a party across the map, a guild meeting, a stage.
 *
 *   THE WINDOW (menu bar → Voice): where you sit (connection, mic, mute, deafen, leave, raise a hand), the roster
 *     with speaking rings and per-voice volume, a moderator's tools (speaker / listener / mod, mute at the SFU, kick
 *     with a ban), your team's rooms (party, guild, city), the public rooms and your invitations, a new room, the
 *     input settings (open mic or push-to-talk, the browser's noise suppression / echo cancellation / gain, the device).
 *   THE PILL: while seated, a small line above the chat — the room, how many, who is speaking, your mic.
 *   PUSH-TO-TALK: the `voicePtt` key (Caps Lock by default; Options → Controls rebinds it). Released on blur. ON A MAC
 *     (the gaps wave, round 2, 2026-09-28) Caps Lock cannot be held — the OS toggles it and the browser sees keyup only at
 *     the next press — so there it is a LATCH whose state is the lock (tap to talk, the key's light on; tap to stop):
 *     lib/voice/ptt.ts. Any other key stays a hold.
 *   TURN (same wave): the hello's ICE servers reach both transports; `localStorage atlas:voice-relay = '1'` forces a
 *     relayed path (the probes' proof of a network that blocks the media port).
 *   RECONNECT: a dropped connection asks for a fresh ticket and reconnects inside the seat's grace; a kick does not.
 *   LISTEN-ONLY (2026-09-28): no microphone (none plugged in, the permission refused) or the "join as a listener"
 *     setting — the join goes ahead without getUserMedia on a receive-only transport, and the window says so and
 *     offers "Use my microphone". Joining used to REQUIRE the microphone: a stage audience member without one could not
 *     even listen.
 * Levers: `__voiceRooms`.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { audioRuntime } from '../lib/audio/runtime.js';
import { keyIs, keyBindingText, keyBindings } from '../lib/keybinds.js';
import { isMacPlatform, pttLatchMode, nextPtt, pttHint } from '../lib/voice/ptt.js';
import * as V from '../lib/voice_room_rules.js';
import { SfuSession, type SfuState } from '../lib/voice/sfu_client.js';
import { el, btn, input, select, tabs, head, chip, muted, field, card, act, debounced } from './town/town_ui.js';

type Ts = { microsSinceUnixEpoch: bigint };
type Id = { toHexString(): string };
interface RoomRow { roomId: bigint; kind: string; refKey: string; name: string; topic: string; owner: Id; access: string; mode: string; capacity: number }
interface SeatRow { memberKey: string; roomId: bigint; identity: Id; role: string; serverMuted: boolean; handRaised: boolean; connected: boolean; joinedAt: Ts }
interface TicketRow { token: string; identity: Id; roomId: bigint; issuedAt: Ts; expiresAt: Ts }
interface InviteRow { inviteKey: string; roomId: bigint; invitee: Id; invitedBy: Id }

const SETTINGS_KEY = 'atlas:voice-rooms';
const TICKET_WAIT_MS = 8000;
const RECONNECT_GIVE_UP_MS = (V.VOICE_SEAT_GRACE_S - 5) * 1000;

interface Settings { ptt: boolean; noise: boolean; echo: boolean; agc: boolean; deviceId: string; listenOnly: boolean }
function loadSettings(): Settings {
  const d: Settings = { ptt: false, noise: true, echo: true, agc: true, deviceId: '', listenOnly: false };
  try { return { ...d, ...(JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') as Partial<Settings>) }; } catch { return d; }
}
const hexOf = (id: Id): string => id.toHexString().replace(/^0x/, '').toLowerCase();
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export class AtlasVoiceRoomsModule implements IModule {
  readonly name = 'AtlasVoiceRooms';
  private win: UiWindow | null = null;
  private pill: HTMLDivElement | null = null;
  private tab = 'room';
  private subbed = false;
  private settings = loadSettings();
  private session: SfuSession | null = null;
  private sessionRoom: bigint | null = null;
  private mic: MediaStream | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private pttHeld = false;
  private selfMuted = false;
  private deafened = false;
  private lastLoud = new Map<string, number>();
  private activeSpeaker = '';
  private sfuState: SfuState | 'idle' = 'idle';
  private status = '';
  /** Why the last join went ahead without a microphone ('' = it has one, or the player chose to listen). */
  private micError = '';
  /** The live session was joined WITHOUT a microphone (set before its connect, so the pill never calls a speaker whose
   *  mic is still being produced "listening only"). */
  private sessionListenOnly = false;
  private leaving = false;
  private reconnectSince = 0; // performance.now() of the drop (never the wall clock: the dev box's clock steps back)
  private reconnecting = false; // one reconnect loop at a time (2026-09-25)
  private busy = false;
  /** The listener's own volume per voice (hex → 0..2). Kept HERE, not on the session: a session is thrown away on
   *  every drop and room switch, and everyone came back at 100% — someone set to 0 was heard again (2026-09-25). */
  private volumes = new Map<string, number>();
  private f = { name: '', access: 'public', mode: 'open', capacity: '12', invite: '' };
  private readonly rerender = debounced(() => this.softRender(), 200);
  /** A Mac (Caps Lock cannot be held there); the probes may force it. */
  private mac = isMacPlatform(typeof navigator === 'undefined' ? null : (navigator as unknown as { platform?: string; userAgent?: string; userAgentData?: { platform?: string } }));

  init(_app: App): void {
    bus.on('atlas:open-voice-rooms', () => this.open());
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    const pill = el('div', 'position:fixed;left:12px;bottom:336px;z-index:31;display:none;cursor:pointer;max-width:360px;padding:4px 10px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 80%, transparent);border:1px solid var(--ax-line2);font:11.5px var(--ax-mono);color:var(--ax-soft);white-space:nowrap;overflow:hidden;text-overflow:ellipsis');
    pill.addEventListener('click', () => this.open());
    document.body.appendChild(pill);
    this.pill = pill;
    window.addEventListener('keydown', (e) => this.onKey(e, true));
    window.addEventListener('keyup', (e) => this.onKey(e, false));
    // THE MAC LATCH: any pointer event that reports the lock OFF closes a latched mic (it was turned off in a text box).
    window.addEventListener('pointerdown', (e) => this.onOther(typeof e.getModifierState === 'function' ? e.getModifierState('CapsLock') : null), true);
    window.addEventListener('blur', () => { const next = nextPtt(this.pttHeld, { kind: 'blur' }, this.pttLatch()); if (next !== this.pttHeld) { this.pttHeld = next; void this.applyMic(); } });
    window.setInterval(() => this.tick(), 250);
    (window as unknown as { __voiceRooms?: unknown }).__voiceRooms = {
      state: () => this.state(),
      open: (tab?: string) => { if (tab) this.tab = tab; this.open(); },
      close: () => this.win?.setOpen(false),
      join: (roomId: number) => this.join(BigInt(roomId)),
      joinTeam: (kind: string) => this.joinTeam(kind),
      create: (name: string, access = 'public', mode = 'open', capacity = 12) => this.create(name, access, mode, capacity),
      leave: () => this.leave(),
      setPtt: (on: boolean) => { this.settings.ptt = on; this.saveSettings(); void this.applyMic(); },
      ptt: (held: boolean) => { this.pttHeld = held; void this.applyMic(); },
      /** Force the platform for the probes (null = detect again). */
      setMac: (on: boolean | null) => { this.mac = on === null ? isMacPlatform(navigator as never) : !!on; this.rerender(); },
      /** The selected ICE candidate type per transport ('relay' = through TURN). */
      iceTypes: () => this.session?.candidateTypes() ?? Promise.resolve(null),
      selfMute: (m: boolean) => { this.selfMuted = m; void this.applyMic(); },
      deafen: (d: boolean) => { this.deafened = d; this.session?.setDeafened(d); },
      setListenOnly: (on: boolean) => { this.settings.listenOnly = on; this.saveSettings(); this.rerender(); },
      useMic: () => this.takeUpMic(),
      /** A network drop, for the probes: the session's socket closes under it and the module reconnects on its own. */
      drop: () => { try { (this.session as unknown as { ws?: WebSocket | null } | null)?.ws?.close(4000, 'probe drop'); } catch { /* none */ } },
      setVolume: (hex: string, v: number) => this.setVoiceVolume(hex, v),
      hearing: () => this.session?.hearing() ?? [],
      stats: () => this.session?.stats(),
      url: () => this.sfuUrl(),
    };
  }

  // ── Rows ──────────────────────────────────────────────────────────────────────
  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try {
      const db = conn.db as unknown as Record<string, { onInsert(f: () => void): void; onUpdate?(f: () => void): void; onDelete(f: () => void): void }>;
      for (const k of ['voiceRoom', 'voiceRoomMember', 'voiceRoomInvite', 'voiceSfuState']) {
        const t = db[k];
        if (!t) continue;
        t.onInsert(() => this.rerender());
        t.onUpdate?.(() => this.rerender());
        t.onDelete(() => this.rerender());
      }
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM voice_room', 'SELECT * FROM voice_room_member', 'SELECT * FROM voice_ticket', 'SELECT * FROM voice_room_invite',
        'SELECT * FROM voice_sfu_state', 'SELECT * FROM party_member WHERE identity = :sender', 'SELECT * FROM guild_member WHERE identity = :sender',
        'SELECT * FROM citizen WHERE identity = :sender',
      ]);
    } catch { /* pre-publish */ }
  }
  private rows<T>(table: string): T[] {
    const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<unknown> }> | undefined)?.[table];
    if (!t) return [];
    try { return [...t.iter()] as T[]; } catch { return []; }
  }
  private me(): string { return (atlas.identityHex ?? '').replace(/^0x/, '').toLowerCase(); }
  private mySeat(): SeatRow | null { const me = this.me(); return this.rows<SeatRow>('voiceRoomMember').find((s) => hexOf(s.identity) === me) ?? null; }
  private room(id: bigint): RoomRow | null { return this.rows<RoomRow>('voiceRoom').find((r) => r.roomId === id) ?? null; }
  private seatsIn(id: bigint): SeatRow[] { return this.rows<SeatRow>('voiceRoomMember').filter((s) => s.roomId === id); }
  /** A traveler's name through the account table's identity index (never a walk of every account — the pill asks
   *  four times a second, the roster once a row). The seat carries the Identity the index wants. */
  private nameOf(hex: string): string {
    const id = this.identityOf(hex);
    const byId = (atlas.conn?.db as unknown as { account?: { identity?: { find(i: Id): { username: string; displayName: string } | undefined } } } | undefined)?.account?.identity;
    let a: { username: string; displayName: string } | undefined;
    try { a = id ? byId?.find(id) : undefined; } catch { a = undefined; }
    return a ? (a.displayName || a.username) : hex.slice(0, 8);
  }
  private identityOf(hex: string): Id | null {
    for (const s of this.rows<SeatRow>('voiceRoomMember')) if (hexOf(s.identity) === hex) return s.identity;
    return null;
  }
  private sfuAlive(): boolean {
    const s = this.rows<{ key: string; lastBeatAt: Ts }>('voiceSfuState').find((r) => r.key === 'sfu');
    return !!s && Date.now() - Number(s.lastBeatAt.microsSinceUnixEpoch / 1000n) < V.VOICE_SFU_STALE_S * 1000;
  }
  private sfuUrl(): string {
    let override = '';
    try { override = localStorage.getItem('atlas:voice-sfu-url') ?? ''; } catch { /* storage denied */ }
    const envUrl = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_ATLAS_SFU_URL ?? '';
    return V.voiceSfuUrl(location, override || envUrl);
  }

  // ── Joining and leaving ───────────────────────────────────────────────────────
  /** The audio context, woken inside the click (the browser's gesture rule). */
  private async ensureAudio(): Promise<void> {
    const rt = audioRuntime();
    rt.unlock();
    if (rt.ctx) await rt.ctx.resume();
  }
  /** The microphone, asked for inside the click, with the input settings. */
  private async ensureMic(): Promise<void> {
    await this.ensureAudio();
    await this.acquireMic();
  }
  /** A join's microphone: none when the player listens only; a refusal or a missing device is NOT a failed join — the
   *  seat is taken as a listener and the window says why (2026-09-28). */
  private async joinMic(): Promise<void> {
    await this.ensureAudio();
    if (this.settings.listenOnly) { this.micError = ''; return; }
    try { await this.acquireMic(); this.micError = ''; } catch (e) {
      const name = (e as { name?: string })?.name ?? '';
      this.micError = name === 'NotAllowedError' ? 'the microphone permission was refused'
        : name === 'NotFoundError' || name === 'OverconstrainedError' ? 'no microphone was found'
          : `the microphone could not start (${String((e as Error)?.message ?? e).slice(0, 60)})`;
      bus.emit('atlas:toast', { text: `Voice: ${this.micError} — you join as a listener`, kind: 'info', ms: 5000 });
    }
  }
  /** "Use my microphone": a listener takes up the mic in the live session (inside the click). */
  private async takeUpMic(): Promise<boolean> {
    try {
      this.settings.listenOnly = false;
      this.saveSettings();
      await this.ensureMic();
      this.micError = '';
      const track = this.mic?.getAudioTracks()[0];
      if (track && this.session) { await this.session.startMic(track); this.sessionListenOnly = !this.session.hasMic; await this.applyMic(); }
      this.rerender();
      return true;
    } catch (e) { this.fail(e); return false; }
  }
  private micLive(): boolean { return !!this.mic && this.mic.getAudioTracks().some((t) => t.readyState === 'live'); }
  /** The track alone (no context resume, which may wait for a gesture): connect() re-takes a mic it lost (2026-09-25). */
  private async acquireMic(): Promise<void> {
    const rt = audioRuntime();
    if (this.micLive()) return;
    this.mic = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: this.settings.echo, noiseSuppression: this.settings.noise, autoGainControl: this.settings.agc, channelCount: 1,
        ...(this.settings.deviceId ? { deviceId: { exact: this.settings.deviceId } } : {}),
      },
    });
    if (rt.ctx) {
      try { this.micSource?.disconnect(); } catch { /* gone */ }
      this.micSource = rt.ctx.createMediaStreamSource(this.mic);
      this.micAnalyser = rt.ctx.createAnalyser();
      this.micAnalyser.fftSize = 512;
      this.micSource.connect(this.micAnalyser); // a meter only — never to the speakers
    }
  }

  /** My tickets' tokens right now. A NEW ticket is one whose token is not in this set: every seat-taking reducer and
   *  requestVoiceTicket mint a fresh token. This replaced a comparison of the server's `issuedAt` / `joinedAt` against
   *  the browser's clock, which refused every ticket once the client clock ran > 2 s ahead of the server (WSL2 steps
   *  its clock after a host sleep) — the server's own refusal is the judge of a stale ticket (2026-09-25). */
  private myTokens(): Set<string> {
    const me = this.me();
    return new Set(this.rows<TicketRow>('voiceTicket').filter((x) => hexOf(x.identity) === me).map((x) => x.token));
  }
  /** The first ticket of mine not in `before` (for `roomId`, or any room when null — a new room's id comes with it). */
  private async waitTicket(before: Set<string>, roomId: bigint | null): Promise<TicketRow> {
    const me = this.me();
    const t0 = performance.now();
    while (performance.now() - t0 < TICKET_WAIT_MS) {
      const t = this.rows<TicketRow>('voiceTicket').find((x) => hexOf(x.identity) === me && !before.has(x.token) && (roomId === null || x.roomId === roomId));
      if (t) return t;
      await sleep(100);
    }
    throw new Error('No ticket came back from the server');
  }

  /** Take a seat through `take` (one of the seat-taking reducers), then connect on the ticket it issued. A connect that
   *  fails after the seat was taken keeps the seat and retries on fresh tickets inside the grace (2026-09-25). */
  private async seatAndConnect(take: () => Promise<unknown> | undefined, roomId: bigint | null): Promise<boolean> {
    if (this.busy) return false;
    this.busy = true;
    let seated = false;
    const hadSession = !!this.session; // a live session dropped silently while busy is picked up again on failure
    const recover = (): void => { const seat = seated || hadSession ? this.mySeat() : null; if (seat) this.retryConnect(seat.roomId); };
    try {
      await this.joinMic(); // never fails the join: no microphone → a listener (2026-09-28)
      const before = this.myTokens();
      if (!(await act(take(), ''))) { recover(); return false; }
      seated = true;
      const t = await this.waitTicket(before, roomId);
      await this.connect(t.roomId, t.token);
      return true;
    } catch (e) {
      this.fail(e);
      recover();
      return false;
    } finally { this.busy = false; }
  }
  private join(roomId: bigint): Promise<boolean> { return this.seatAndConnect(() => atlas.conn?.reducers.joinVoiceRoom({ roomId }), roomId); }
  private joinTeam(kind: string): Promise<boolean> { return this.seatAndConnect(() => atlas.conn?.reducers.joinTeamVoice({ kind }), null); }
  private create(name: string, access: string, mode: string, capacity: number): Promise<boolean> {
    return this.seatAndConnect(() => atlas.conn?.reducers.createVoiceRoom({ name, access, mode, capacity }), null);
  }

  private async connect(roomId: bigint, ticket: string): Promise<void> {
    this.leaving = false;
    // Detach the old session BEFORE closing it (2026-09-25): closed while still `this.session`, its onSessionClosed saw
    // the seat in another room, toasted "Switching rooms" and stopped the mic, so the new room was joined listen-only.
    const old = this.session;
    this.session = null;
    old?.close('Switching rooms');
    const rt = audioRuntime();
    const out = rt.busInput('voice');
    if (!rt.ctx || !out) throw new Error('No audio device');
    // A mic lost under a switch is taken again (the permission is already granted); a refusal leaves us listening.
    if (!this.micLive() && !this.settings.listenOnly && !this.micError) { try { await this.acquireMic(); } catch { /* listen-only */ } }
    const s = new SfuSession(this.sfuUrl(), rt.ctx, out, {
      levels: (list) => { const now = performance.now(); for (const [hex, db] of list) if (V.voiceLoud(db)) this.lastLoud.set(hex, now); },
      activeSpeaker: (hex) => { this.activeSpeaker = hex; },
      you: () => { void this.applyMic(); this.rerender(); },
      peerJoined: () => this.rerender(),
      peerLeft: (hex) => { this.lastLoud.delete(hex); this.rerender(); },
      peerState: () => this.rerender(),
      voiceAdded: () => this.rerender(),
      voiceRemoved: () => this.rerender(),
      state: (st) => { this.sfuState = st; this.rerender(); },
      closed: (reason, kicked) => this.onSessionClosed(s, reason, kicked),
    });
    this.session = s;
    this.sessionRoom = roomId;
    s.setDeafened(this.deafened);
    for (const [hex, v] of this.volumes) s.setVolume(hex, v); // the listener's volumes outlive the session
    try {
      const track = this.settings.listenOnly ? undefined : this.mic?.getAudioTracks()[0];
      this.sessionListenOnly = !track;
      // listen-only: a receive-only transport (2026-09-28); `atlas:voice-relay` forces a relayed (TURN) path.
      await s.connect(ticket, { send: !!track, relay: this.relayForced() });
      if (track) await s.startMic(track);
    } catch (e) {
      // A failed connect (the hello timing out, the device load or a transport failing with the socket still open)
      // left a dead `this.session` that blocked every reconnect and hid the Connect button (2026-09-25).
      if (this.session === s) this.session = null;
      s.close('The voice connection failed');
      throw e;
    }
    this.reconnectSince = 0;
    this.status = '';
    await this.applyMic();
    this.rerender();
  }

  private onSessionClosed(s: SfuSession, reason: string, kicked: boolean): void {
    if (this.session !== s) return;
    this.session = null;
    this.rerender();
    if (this.leaving) { this.sessionRoom = null; return; }
    // A join / create / team join is under way: this session's seat moved ON PURPOSE — the SFU closes the old room's
    // connection the moment the seat moves, usually before the new ticket arrives — so no toast, and the mic stays for
    // the new room (it was stopped here, and the new room was joined listen-only) (2026-09-25).
    if (this.busy) { this.sessionRoom = null; return; }
    const seat = this.mySeat();
    if (kicked || !seat || seat.roomId !== this.sessionRoom) {
      this.sessionRoom = null;
      this.status = reason;
      bus.emit('atlas:toast', { text: `Voice: ${reason}`, kind: kicked ? 'error' : 'info', ms: 5000 });
      this.stopMic();
      return;
    }
    // A drop: a fresh ticket and a reconnect, while the seat's grace lasts.
    this.retryConnect(seat.roomId);
  }

  /** Reconnect to the seat we hold on fresh tickets while its grace lasts (a drop, or a join whose connect failed). */
  private retryConnect(roomId: bigint): void {
    if (this.leaving || this.session) return;
    const seat = this.mySeat();
    if (!seat || seat.roomId !== roomId) return;
    this.sessionRoom = roomId;
    if (!this.reconnectSince) this.reconnectSince = performance.now();
    this.sfuState = 'reconnecting';
    this.rerender();
    void this.reconnect(roomId);
  }

  private async reconnect(roomId: bigint): Promise<void> {
    // One loop at a time: a connect that fails inside the loop also closes its session, and that close asked for a
    // second loop racing the first for the same seat (2026-09-25).
    if (this.reconnecting) return;
    this.reconnecting = true;
    try {
      let wait = 1000;
      while (!this.leaving && performance.now() - this.reconnectSince < RECONNECT_GIVE_UP_MS) {
        await sleep(wait);
        const seat = this.mySeat();
        if (!seat || seat.roomId !== roomId || this.session) return;
        try {
          const before = this.myTokens();
          await atlas.conn?.reducers.requestVoiceTicket({ roomId });
          await this.connect(roomId, (await this.waitTicket(before, roomId)).token);
          bus.emit('atlas:toast', { text: 'Voice reconnected', kind: 'success', ms: 2500 });
          return;
        } catch { wait = Math.min(8000, wait * 2); }
      }
      if (!this.leaving && !this.session) {
        // Gave up: the budget is spent for THIS drop only, and the pill stops saying "reconnecting…" (2026-09-25).
        this.status = 'Lost the voice server'; this.sessionRoom = null; this.reconnectSince = 0; this.sfuState = 'idle';
        this.stopMic(); this.rerender();
      }
    } finally { this.reconnecting = false; }
  }

  private async leave(): Promise<void> {
    this.leaving = true;
    this.session?.close('You left');
    this.session = null;
    this.sessionRoom = null;
    this.reconnectSince = 0; // a stale drop budget never carries into the next room (2026-09-25)
    this.sfuState = 'idle';
    this.stopMic();
    await act(atlas.conn?.reducers.leaveVoiceRoom({}), '');
    this.rerender();
  }

  /** The listener's volume for one voice: kept in the module and applied to the live session (2026-09-25). */
  private setVoiceVolume(hex: string, v: number): void {
    const g = Math.max(0, Math.min(2, Number.isFinite(v) ? v : 1));
    this.volumes.set(hex, g);
    this.session?.setVolume(hex, g);
  }

  private stopMic(): void {
    for (const t of this.mic?.getTracks() ?? []) t.stop();
    this.mic = null;
    try { this.micSource?.disconnect(); } catch { /* gone */ }
    this.micSource = null;
    this.micAnalyser = null;
  }

  private fail(e: unknown): void {
    const msg = String((e as Error)?.message ?? e).replace(/^SenderError:\s*/, '');
    this.status = msg;
    bus.emit('atlas:toast', { text: `Voice: ${msg}`, kind: 'error', ms: 5000 });
    this.rerender();
  }

  // ── The microphone ────────────────────────────────────────────────────────────
  private canSpeakNow(): { canSpeak: boolean; serverMuted: boolean } {
    const seat = this.mySeat();
    const room = seat ? this.room(seat.roomId) : null;
    return { canSpeak: !!seat && !!room && V.voiceCanSpeak(room.mode, seat.role), serverMuted: !!seat?.serverMuted };
  }
  private micShouldOpen(): boolean {
    const c = this.canSpeakNow();
    return V.voiceMicOpen({ ...c, selfMuted: this.selfMuted, pushToTalk: this.settings.ptt, pttHeld: this.pttHeld });
  }
  private async applyMic(): Promise<void> {
    const s = this.session;
    if (!s || !s.hasMic) return;
    const open = this.micShouldOpen();
    if (open !== s.micOpen) await s.setMicOpen(open);
  }
  /** Caps Lock push-to-talk is a latch on a Mac (lib/voice/ptt.ts). */
  private pttLatch(): boolean { return pttLatchMode(this.mac, keyBindings().voicePtt ?? []); }
  private relayForced(): boolean { try { return localStorage.getItem('atlas:voice-relay') === '1'; } catch { return false; } }
  private onKey(e: KeyboardEvent, down: boolean): void {
    const isPtt = keyIs(e.code, 'voicePtt');
    const latch = this.pttLatch();
    if (!isPtt && !latch) return;
    const t = e.target as HTMLElement | null;
    const inField = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable === true);
    const capsOn = typeof e.getModifierState === 'function' ? e.getModifierState('CapsLock') : null;
    const next = nextPtt(this.pttHeld, { kind: 'key', down, code: e.code, isPtt, capsOn, inField, repeat: e.repeat }, latch);
    if (next === this.pttHeld) return;
    this.pttHeld = next;
    if (this.settings.ptt && this.session) { if (isPtt) e.preventDefault(); void this.applyMic(); this.updatePill(); }
  }
  private onOther(capsOn: boolean | null): void {
    if (!this.pttLatch()) return;
    const next = nextPtt(this.pttHeld, { kind: 'other', capsOn }, true);
    if (next === this.pttHeld) return;
    this.pttHeld = next;
    if (this.settings.ptt && this.session) { void this.applyMic(); this.updatePill(); }
  }
  private micBuf: Float32Array<ArrayBuffer> | null = null;
  private micLevel(): number {
    const a = this.micAnalyser;
    if (!a) return 0;
    if (!this.micBuf || this.micBuf.length !== a.fftSize) this.micBuf = new Float32Array(a.fftSize);
    const buf = this.micBuf;
    a.getFloatTimeDomainData(buf);
    let s = 0;
    for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
    return Math.sqrt(s / buf.length);
  }

  // ── The pill and the tick ─────────────────────────────────────────────────────
  private speaking(hex: string): boolean { return V.voiceSpeaking(this.lastLoud.get(hex) ?? -1e9, performance.now()); }
  private tick(): void {
    this.updatePill();
    if (this.win?.isOpen()) this.updateLive();
    // The seat went away under us (a sweep, another tab): drop the session.
    if (this.session && this.sessionRoom !== null && !this.leaving) {
      const seat = this.mySeat();
      if (!seat && this.session.state === 'connected') { /* the SFU closes us within a second; nothing to do */ }
    }
  }
  private updatePill(): void {
    const p = this.pill;
    if (!p) return;
    const seat = this.mySeat();
    const room = seat ? this.room(seat.roomId) : null;
    const show = !!room && (!!this.session || this.sfuState === 'reconnecting');
    if ((p.style.display !== 'none') !== show) p.style.display = show ? 'block' : 'none';
    if (!show || !room) return;
    const here = this.seatsIn(room.roomId).length;
    const talking = this.seatsIn(room.roomId).map((s) => hexOf(s.identity)).filter((h) => this.speaking(h)).map((h) => this.nameOf(h));
    const mic = this.micLabel();
    const txt = `◉ ${room.name} · ${here} · ${mic}${talking.length ? ` · ${talking.join(', ')}` : ''}${this.sfuState === 'reconnecting' ? ' · reconnecting…' : ''}`;
    if (p.textContent !== txt) p.textContent = txt;
    p.style.color = this.session?.micOpen ? 'var(--ax-good)' : 'var(--ax-soft)';
  }
  private micLabel(): string {
    const c = this.canSpeakNow();
    if (!c.canSpeak) return 'audience';
    if (this.session && this.sessionListenOnly) return 'listening only';
    if (c.serverMuted) return 'muted by a moderator';
    if (this.selfMuted) return 'muted';
    if (this.settings.ptt) return pttHint(this.pttLatch(), this.pttHeld, keyBindingText('voicePtt'));
    return 'open mic';
  }

  // ── The window ────────────────────────────────────────────────────────────────
  private open(): void {
    if (!this.win) {
      this.win = makeWindow({ id: 'voice-rooms', title: 'Voice rooms', icon: 'mic', width: 460, minWidth: 360, height: 600 });
      this.win.body.addEventListener('focusout', () => window.setTimeout(() => this.softRender(), 0));
    }
    this.subscribe();
    this.win.setOpen(true);
    this.render();
  }
  private softRender(): void {
    const a = document.activeElement as HTMLElement | null;
    if (a && this.win?.body.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'SELECT')) return;
    this.render();
  }
  /** The live bits (rings, the meter) without a re-render. */
  private updateLive(): void {
    const body = this.win?.body;
    if (!body) return;
    for (const dot of body.querySelectorAll<HTMLElement>('[data-voice-ring]')) {
      const on = this.speaking(dot.dataset.voiceRing ?? '');
      dot.style.background = on ? 'var(--ax-good)' : 'var(--ax-line2)';
      dot.style.boxShadow = on ? '0 0 0 3px color-mix(in srgb, var(--ax-good) 35%, transparent)' : 'none';
    }
    const meter = body.querySelector<HTMLElement>('[data-voice-meter]');
    if (meter) meter.style.width = `${Math.min(100, Math.round(this.micLevel() * 400))}%`;
  }

  private render(): void {
    const body = this.win?.body;
    if (!body || !this.win?.isOpen()) return;
    const scroll = body.scrollTop;
    body.replaceChildren();
    const wrap = el('div', 'display:flex;flex-direction:column;gap:10px;padding:12px 14px');
    if (!this.sfuAlive()) wrap.appendChild(el('div', 'color:var(--ax-warn);font-size:12.5px', 'The voice server is not running — rooms can be joined, but no audio will flow until it is back.'));
    wrap.appendChild(tabs([{ key: 'room', label: 'Room' }, { key: 'find', label: 'Find a room' }, { key: 'settings', label: 'Settings' }], this.tab, (k) => { this.tab = k; this.render(); }));
    const pane = el('div', 'display:flex;flex-direction:column;gap:10px');
    pane.dataset.voiceTab = this.tab;
    if (this.tab === 'room') this.paneRoom(pane); else if (this.tab === 'find') this.paneFind(pane); else this.paneSettings(pane);
    wrap.appendChild(pane);
    body.appendChild(wrap);
    body.scrollTop = scroll;
    this.updateLive();
  }

  private paneRoom(p: HTMLElement): void {
    const seat = this.mySeat();
    const room = seat ? this.room(seat.roomId) : null;
    if (!seat || !room) {
      p.appendChild(muted('You are not in a voice room. Join your party, guild or city, or find a room.'));
      this.teamButtons(p);
      if (this.status) p.appendChild(el('div', 'color:var(--ax-warn);font-size:12.5px', this.status));
      return;
    }
    const c = card();
    c.appendChild(el('div', 'font:600 15px var(--ax-font);color:var(--ax-hi)', room.name));
    if (room.topic) c.appendChild(muted(room.topic));
    const chips = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    chips.appendChild(chip(this.session ? (this.sfuState === 'connected' ? 'connected' : this.sfuState) : seat.connected ? 'connected elsewhere' : 'not connected', this.session && this.sfuState === 'connected' ? 'good' : 'warn'));
    chips.appendChild(chip(room.mode === 'stage' ? 'stage' : 'open room'));
    chips.appendChild(chip(`${room.kind === 'open' ? room.access : room.kind}`));
    chips.appendChild(chip(`you: ${seat.role}`));
    chips.appendChild(chip(this.micLabel(), this.session?.micOpen ? 'good' : ''));
    c.appendChild(chips);
    if (this.session && this.sessionListenOnly) {
      // LISTEN-ONLY, said out loud (2026-09-28): why there is no microphone, and the way to take it up.
      const why = this.micError || (this.settings.listenOnly ? 'you joined as a listener' : 'no microphone');
      const lo = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap');
      lo.dataset.voiceListenOnly = '1';
      lo.appendChild(el('span', 'color:var(--ax-muted);font-size:12px', `Listening only — ${why}. Everyone here is heard; nobody hears you.`));
      if (V.voiceCanSpeak(room.mode, seat.role)) lo.appendChild(btn('Use my microphone', () => { void this.takeUpMic(); }));
      c.appendChild(lo);
    }
    const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    if (!this.session) row.appendChild(btn('Connect', () => { void this.join(room.roomId); }, true));
    row.appendChild(btn(this.selfMuted ? 'Unmute' : 'Mute', () => { this.selfMuted = !this.selfMuted; void this.applyMic(); this.render(); }));
    row.appendChild(btn(this.deafened ? 'Undeafen' : 'Deafen', () => { this.deafened = !this.deafened; this.session?.setDeafened(this.deafened); this.render(); }));
    if (room.mode === 'stage' && seat.role === 'listener') row.appendChild(btn(seat.handRaised ? 'Lower hand' : 'Raise hand', () => { void act(atlas.conn?.reducers.raiseVoiceHand({ roomId: room.roomId, up: !seat.handRaised }), ''); }));
    row.appendChild(btn('Leave', () => { void this.leave(); }));
    c.appendChild(row);
    p.appendChild(c);
    // The roster.
    const seats = this.seatsIn(room.roomId).map((s) => ({ s, hex: hexOf(s.identity), name: this.nameOf(hexOf(s.identity)) }));
    seats.sort((a, b) => V.voiceRosterOrder({ role: a.s.role, handRaised: a.s.handRaised, name: a.name }, { role: b.s.role, handRaised: b.s.handRaised, name: b.name }));
    p.appendChild(head(`In the room (${seats.length}/${room.capacity})`));
    const mod = V.voiceCanModerate(seat.role);
    for (const { s, hex, name } of seats) {
      const r = el('div', 'display:flex;flex-direction:column;gap:4px;padding:6px 8px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);background:var(--ax-bg2)');
      r.dataset.voicePeer = hex;
      const top = el('div', 'display:flex;gap:8px;align-items:center');
      const ring = el('span', 'width:10px;height:10px;border-radius:50%;background:var(--ax-line2);flex:none');
      ring.dataset.voiceRing = hex;
      top.appendChild(ring);
      top.appendChild(el('span', 'color:var(--ax-text);flex:1', `${name}${hex === this.me() ? ' (you)' : ''}`));
      if (s.role !== 'speaker') top.appendChild(chip(s.role, s.role === 'owner' || s.role === 'mod' ? 'info' : ''));
      if (s.handRaised) top.appendChild(chip('✋ hand up', 'warn'));
      if (s.serverMuted) top.appendChild(chip('muted by mod', 'bad'));
      if (!s.connected) top.appendChild(chip('connecting', 'warn'));
      r.appendChild(top);
      if (hex !== this.me()) {
        const ctl = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap');
        const vol = document.createElement('input');
        vol.type = 'range'; vol.min = '0'; vol.max = '200'; vol.step = '5';
        vol.value = String(Math.round((this.volumes.get(hex) ?? 1) * 100)); // the module's map outlives sessions (2026-09-25)
        vol.style.width = '120px';
        vol.title = 'Your volume for this voice';
        vol.addEventListener('input', () => this.setVoiceVolume(hex, Number(vol.value) / 100));
        ctl.appendChild(el('span', 'color:var(--ax-muted);font-size:11.5px', 'volume'));
        ctl.appendChild(vol);
        if (mod && V.voiceMayDiscipline(seat.role, s.role, false)) {
          const id = s.identity;
          if (room.mode === 'stage' && V.voiceMaySetRole(seat.role, s.role, s.role === 'listener' ? 'speaker' : 'listener', false)) {
            ctl.appendChild(btn(s.role === 'listener' ? 'Invite to speak' : 'Move to audience', () => { void act(atlas.conn?.reducers.setVoiceRole({ roomId: room.roomId, target: id as never, role: s.role === 'listener' ? 'speaker' : 'listener' }), ''); }));
          }
          if (seat.role === 'owner' && s.role !== 'mod') ctl.appendChild(btn('Make mod', () => { void act(atlas.conn?.reducers.setVoiceRole({ roomId: room.roomId, target: id as never, role: 'mod' }), ''); }));
          if (seat.role === 'owner' && s.role === 'mod') ctl.appendChild(btn('Unmod', () => { void act(atlas.conn?.reducers.setVoiceRole({ roomId: room.roomId, target: id as never, role: room.mode === 'stage' ? 'listener' : 'speaker' }), ''); }));
          ctl.appendChild(btn(s.serverMuted ? 'Unmute' : 'Mute for all', () => { void act(atlas.conn?.reducers.setVoiceServerMute({ roomId: room.roomId, target: id as never, muted: !s.serverMuted }), ''); }));
          ctl.appendChild(btn('Kick (10 min)', () => { void act(atlas.conn?.reducers.kickFromVoiceRoom({ roomId: room.roomId, target: id as never, banMinutes: 10 }), `${name} removed`); }));
        }
        r.appendChild(ctl);
      }
      p.appendChild(r);
    }
    // Invite / owner settings.
    if (mod || room.access === 'public') {
      p.appendChild(head('Invite'));
      const who = input('Username', this.f.invite, 'text', '180px');
      who.addEventListener('input', () => { this.f.invite = who.value; });
      p.appendChild(field('Traveler', who, btn('Invite', () => { if (this.f.invite.trim()) void act(atlas.conn?.reducers.inviteToVoiceRoom({ roomId: room.roomId, username: this.f.invite.trim() }), `Invited ${this.f.invite.trim()}`); })));
    }
    if (hexOf(room.owner) === this.me()) {
      p.appendChild(head('Your room'));
      const row2 = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
      row2.appendChild(btn(room.mode === 'stage' ? 'Make it an open room' : 'Make it a stage', () => {
        void act(atlas.conn?.reducers.setVoiceRoom({ roomId: room.roomId, name: room.name, topic: room.topic, mode: room.mode === 'stage' ? 'open' : 'stage', access: room.access, capacity: room.capacity }), '');
      }));
      if (room.kind === 'open') row2.appendChild(btn('Close the room', () => { void act(atlas.conn?.reducers.closeVoiceRoom({ roomId: room.roomId }), 'Room closed'); }));
      p.appendChild(row2);
    }
  }

  private teamButtons(p: HTMLElement): void {
    const me = this.me();
    const inParty = this.rows<{ identity: Id }>('partyMember').some((m) => hexOf(m.identity) === me);
    const inGuild = this.rows<{ identity: Id }>('guildMember').some((m) => hexOf(m.identity) === me);
    const inCity = this.rows<{ identity: Id }>('citizen').some((m) => hexOf(m.identity) === me);
    const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    const b1 = btn('Party voice', () => { void this.joinTeam('party'); }, inParty); b1.disabled = !inParty;
    const b2 = btn('Guild voice', () => { void this.joinTeam('guild'); }); b2.disabled = !inGuild;
    const b3 = btn('City voice', () => { void this.joinTeam('city'); }); b3.disabled = !inCity;
    row.append(b1, b2, b3);
    p.appendChild(row);
  }

  private paneFind(p: HTMLElement): void {
    this.teamButtons(p);
    const me = this.me();
    const invited = new Set(this.rows<InviteRow>('voiceRoomInvite').filter((i) => hexOf(i.invitee) === me).map((i) => i.roomId));
    const mySeat = this.mySeat();
    const list = this.rows<RoomRow>('voiceRoom').filter((r) => r.kind === 'open' && (r.access === 'public' || invited.has(r.roomId) || hexOf(r.owner) === me));
    list.sort((a, b) => this.seatsIn(b.roomId).length - this.seatsIn(a.roomId).length || a.name.localeCompare(b.name));
    p.appendChild(head(`Rooms (${list.length})`));
    if (!list.length) p.appendChild(muted('No open rooms yet — make one below.'));
    for (const r of list) {
      const c = card();
      c.dataset.voiceRoom = String(r.roomId);
      const top = el('div', 'display:flex;gap:8px;align-items:center');
      top.appendChild(el('span', 'color:var(--ax-text);flex:1', r.name));
      top.appendChild(chip(`${this.seatsIn(r.roomId).length}/${r.capacity}`));
      if (r.mode === 'stage') top.appendChild(chip('stage', 'info'));
      if (r.access === 'invite') top.appendChild(chip(invited.has(r.roomId) ? 'invited' : 'invite only', 'warn'));
      const here = mySeat?.roomId === r.roomId;
      top.appendChild(btn(here ? 'You are here' : 'Join', () => { if (!here) void this.join(r.roomId).then((ok) => { if (ok) { this.tab = 'room'; this.render(); } }); }, !here));
      c.appendChild(top);
      if (r.topic) c.appendChild(muted(r.topic));
      p.appendChild(c);
    }
    p.appendChild(head('Make a room'));
    const nm = input('Room name', this.f.name, 'text', '200px');
    nm.addEventListener('input', () => { this.f.name = nm.value; });
    const acc = select([{ value: 'public', label: 'Public' }, { value: 'invite', label: 'Invite only' }], this.f.access);
    acc.addEventListener('change', () => { this.f.access = acc.value; });
    const mode = select([{ value: 'open', label: 'Everyone speaks' }, { value: 'stage', label: 'Stage (speakers + audience)' }], this.f.mode);
    mode.addEventListener('change', () => { this.f.mode = mode.value; });
    const cap = input('Seats', this.f.capacity, 'number', '70px');
    cap.addEventListener('input', () => { this.f.capacity = cap.value; });
    p.appendChild(field('Name', nm));
    p.appendChild(field('Who', acc, mode));
    p.appendChild(field('Seats', cap));
    p.appendChild(btn('Make and join', () => {
      void this.create(this.f.name, this.f.access, this.f.mode, Number(this.f.capacity) || 12).then((ok) => { if (ok) { this.tab = 'room'; this.f.name = ''; this.render(); } });
    }, true));
    p.appendChild(muted(`Audio travels through the voice server, never peer to peer — no one learns your address. A blocked traveler never hears you and you never hear them.`));
  }

  private paneSettings(p: HTMLElement): void {
    p.appendChild(head('Talking'));
    const modeSel = select([{ value: 'open', label: 'Open mic' }, { value: 'ptt', label: `Push to talk (${keyBindingText('voicePtt')})` }], this.settings.ptt ? 'ptt' : 'open');
    modeSel.addEventListener('change', () => { this.settings.ptt = modeSel.value === 'ptt'; this.saveSettings(); void this.applyMic(); modeSel.blur(); this.render(); });
    p.appendChild(field('Mode', modeSel));
    p.appendChild(muted('Push to talk is rebindable in Options → Controls ("Push to talk (voice rooms)").'));
    if (this.pttLatch()) {
      // THE MAC LATCH, said where the player sets it (2026-09-28).
      const note = muted('On a Mac, Caps Lock cannot be held — the system toggles it. Here it works as a switch: tap Caps Lock to talk (its light comes on), tap it again to stop. Leaving the game window closes the mic; tap twice to talk again. Bind another key in Options → Controls for a true hold.');
      note.dataset.voicePttMac = '1';
      p.appendChild(note);
    }
    const meter = el('div', 'height:8px;border-radius:var(--ax-radius);background:var(--ax-bg3);overflow:hidden;width:220px');
    const bar = el('div', 'height:100%;width:0%;background:var(--ax-accent)');
    bar.dataset.voiceMeter = '1';
    meter.appendChild(bar);
    p.appendChild(field('Your level', meter));
    p.appendChild(head('Microphone'));
    const lol = el('label', 'display:flex;gap:6px;align-items:center;color:var(--ax-soft)');
    const locb = document.createElement('input'); locb.type = 'checkbox'; locb.checked = this.settings.listenOnly;
    locb.dataset.voiceListenOnly = '1';
    locb.addEventListener('change', () => { this.settings.listenOnly = locb.checked; this.saveSettings(); });
    lol.append(locb, document.createTextNode('Join as a listener (no microphone) — you can take the mic up in the room'));
    p.appendChild(lol);
    for (const [key, label] of [['noise', 'Noise suppression'], ['echo', 'Echo cancellation'], ['agc', 'Automatic gain']] as const) {
      const lab = el('label', 'display:flex;gap:6px;align-items:center;color:var(--ax-soft)');
      const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = this.settings[key];
      cb.addEventListener('change', () => { this.settings[key] = cb.checked; this.saveSettings(); void this.reacquire(); });
      lab.append(cb, document.createTextNode(label));
      p.appendChild(lab);
    }
    const devSel = select([{ value: '', label: 'Default microphone' }], this.settings.deviceId);
    devSel.addEventListener('change', () => { this.settings.deviceId = devSel.value; this.saveSettings(); void this.reacquire(); devSel.blur(); });
    void navigator.mediaDevices?.enumerateDevices?.().then((list) => {
      for (const d of list) if (d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'default') {
        const o = document.createElement('option'); o.value = d.deviceId; o.textContent = d.label || `Microphone ${d.deviceId.slice(0, 6)}`;
        devSel.appendChild(o);
      }
      devSel.value = this.settings.deviceId;
    }).catch(() => { /* no permission yet */ });
    p.appendChild(field('Device', devSel));
    p.appendChild(muted('The browser applies noise suppression, echo cancellation and gain before your voice is sent. Room voices play on the Voice chat bus — its level is in Options → Sound.'));
  }

  /** A new microphone with the new settings, swapped in without renegotiating. */
  private async reacquire(): Promise<void> {
    const had = !!this.mic;
    this.stopMic();
    if (!had && !this.session) return;
    if (!had && this.session && this.sessionListenOnly) return; // a listener stays a listener until "Use my microphone" (2026-09-28)
    try {
      await this.ensureMic();
      const track = this.mic?.getAudioTracks()[0];
      if (track && this.session) await this.session.replaceTrack(track);
    } catch (e) { this.fail(e); }
  }

  private saveSettings(): void { try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.settings)); } catch { /* storage denied */ } }

  private state(): Record<string, unknown> {
    const seat = this.mySeat();
    return {
      sfuAlive: this.sfuAlive(), sfuState: this.sfuState, url: this.sfuUrl(), roomId: seat ? Number(seat.roomId) : null, role: seat?.role ?? null,
      connected: seat?.connected ?? false, session: !!this.session, micOpen: this.session?.micOpen ?? false, hasMic: this.session?.hasMic ?? false,
      voices: this.session?.voiceCount ?? 0, listenOnly: this.settings.listenOnly, micError: this.micError, ptt: this.settings.ptt, pttHeld: this.pttHeld, selfMuted: this.selfMuted, deafened: this.deafened,
      mac: this.mac, pttLatch: this.pttLatch(), relay: this.relayForced(),
      speaking: [...this.lastLoud.keys()].filter((h) => this.speaking(h)), activeSpeaker: this.activeSpeaker, status: this.status,
      lastError: this.session?.lastError ?? '', pill: this.pill?.style.display !== 'none' ? this.pill?.textContent : null,
      links: this.session?.links ?? null, audio: audioRuntime().ctx?.state ?? 'none',
    };
  }

  dispose(): void {
    this.leaving = true;
    this.session?.close('Closing');
    this.stopMic();
    this.pill?.remove();
    this.win?.root.remove();
  }
}
